// Onderdelen van de calculatiepagina uit SPEC_calculatietool_wijzigingen_27okt2026:
//  - blok Prijsafwijkingen t.o.v. standaardprijzen (2), met de verplichte reden;
//  - status "open voor oplevering" per post en het blok open posten met vrijgave (4);
//  - versies van de calculatie (SPEC_workflow 5.1);
//  - posten verschuiven (SPEC_workflow 7.2).
// Alles hier is intern: niets komt op de PDF voor de onderaannemer, behalve de open posten.
import { useMemo, useState } from 'react';
import {
  computeTotals,
  redenGeldig,
  vindPrijsafwijkingen,
  zelfdeAfwijking,
  PRIJSAFWIJKING_REDEN_MIN,
  type GevelConfig,
  type MatchedLine,
  type MeerwerkLine,
  type Oplevering,
  type OpenPost,
  type Prijsafwijking,
  type PricelistItem,
} from '@recotex/shared';
import type { PrijsafwijkingRij, VersieRij, VrijgaveRij } from '../lib/api';
import { api } from '../lib/api';
import { fmtDate, fmtDateTime, fmtEUR, fmtNum } from '../lib/format';

// === Prijsafwijkingen (27okt 2) ===

export interface LiveAfwijking extends Prijsafwijking {
  door: string | null;
  op: string | null;
  /** Al bewaard of al van vóór de historiek: geen nieuwe reden nodig. */
  bekend: boolean;
  vanVoorHistoriek: boolean;
}

/** Afwijkingen van de huidige (eventueel onopgeslagen) lijnen, verrijkt met wat de server al bewaarde. */
export function useLiveAfwijkingen(input: {
  matchedLines: MatchedLine[];
  meerwerkLines: MeerwerkLine[];
  pricelistItems: PricelistItem[];
  gevel: GevelConfig | null;
  opgeslagen?: PrijsafwijkingRij[];
}): LiveAfwijking[] {
  const { matchedLines, meerwerkLines, pricelistItems, gevel, opgeslagen } = input;
  return useMemo(() => {
    if (!pricelistItems.length) {
      // Prijslijst nog niet geladen: toon wat de server berekende.
      return (opgeslagen ?? []).map((o) => ({ ...o, bekend: true }));
    }
    const byId = new Map(pricelistItems.map((it) => [it.id, it] as const));
    const gevelResultaat = gevel ? computeTotals({ matchedLines, meerwerkLines, extraRegels: [], gevel }).gevel : null;
    const live = vindPrijsafwijkingen(matchedLines, meerwerkLines, {
      itemPrijs: (id) => {
        const it = byId.get(id);
        return it && !it.isNvt ? it.prijs : null;
      },
      gevel,
      gevelResultaat,
    });
    return live.map((a) => {
      const o = (opgeslagen ?? []).find((x) => zelfdeAfwijking(a, x));
      return {
        ...a,
        reden: a.reden || o?.reden || '',
        door: o?.door ?? null,
        op: o?.op ?? null,
        bekend: !!o,
        vanVoorHistoriek: !!o?.vanVoorHistoriek,
      };
    });
  }, [matchedLines, meerwerkLines, pricelistItems, gevel, opgeslagen]);
}

/** Afwijkingen die nog een (geldige) reden nodig hebben vóór het opslaan. */
export function afwijkingenZonderReden(live: LiveAfwijking[]): LiveAfwijking[] {
  return live.filter((a) => !a.bekend && !redenGeldig(a.reden));
}

/** Zet de reden op de werkpost of het meerwerk waar de afwijking bij hoort. */
export function zetReden<T extends { id: string; prijsAfwijkingReden?: string }>(lijnen: T[], lijnId: string, reden: string): T[] {
  return lijnen.map((l) => (l.id === lijnId ? { ...l, prijsAfwijkingReden: reden } : l));
}

function pct(n: number | null): string {
  if (n == null) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(2).replace('.', ',')}%`;
}

export function PrijsafwijkingenBlok({ afwijkingen, calculatieTotaal, editing, onReden, toonFouten }: {
  afwijkingen: LiveAfwijking[];
  calculatieTotaal: number;
  editing: boolean;
  onReden?: (a: LiveAfwijking, reden: string) => void;
  toonFouten?: boolean;
}) {
  const impact = Math.round(afwijkingen.reduce((s, a) => s + a.impact, 0) * 100) / 100;
  const impactPct = calculatieTotaal > 0 ? (impact / calculatieTotaal) * 100 : null;
  const kleur = (n: number) => (n > 0 ? 'text-danger' : n < 0 ? 'text-success' : '');
  return (
    <div className="card mb-5" aria-labelledby="prijsafwijkingen-titel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <h3 id="prijsafwijkingen-titel" className="text-lg font-bold">Prijsafwijkingen t.o.v. standaardprijzen</h3>
        <span className="text-xs text-secondary normal-case tracking-normal">Intern — komt niet op de PDF voor de onderaannemer</span>
      </div>
      {!afwijkingen.length ? (
        <p className="text-sm text-secondary normal-case tracking-normal">Geen prijsafwijkingen. Alle eenheidsprijzen volgen de prijslijst van de onderaannemer.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] normal-case tracking-normal">
            <thead>
              <tr className="thead-row">
                <th className="px-1.5 py-2 min-w-[150px]">Post</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Hoeveelheid</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Standaard</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Toegepast</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Verschil/eenheid</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Verschil %</th>
                <th className="px-1.5 py-2 text-right whitespace-nowrap">Impact totaal</th>
                <th className="px-1.5 py-2">Reden</th>
                <th className="px-1.5 py-2 whitespace-nowrap">Door, datum</th>
              </tr>
            </thead>
            <tbody>
              {afwijkingen.map((a) => {
                const fout = toonFouten && !a.bekend && !redenGeldig(a.reden);
                return (
                  <tr key={a.regelSleutel} className="border-b border-lightgrey align-top">
                    <td className="px-1.5 py-2 font-semibold">{a.post}{a.soort === 'meerwerk' && <span className="text-xs text-secondary"> (meerwerk)</span>}</td>
                    <td className="px-1.5 py-2 text-right tabular-nums whitespace-nowrap">{fmtNum(a.hoeveelheid)} {a.eenheid}</td>
                    <td className="px-1.5 py-2 text-right tabular-nums whitespace-nowrap">{fmtEUR(a.standaard)}</td>
                    <td className="px-1.5 py-2 text-right tabular-nums whitespace-nowrap">{fmtEUR(a.toegepast)}</td>
                    <td className={`px-1.5 py-2 text-right tabular-nums whitespace-nowrap font-bold ${kleur(a.verschilPerEenheid)}`}>{a.verschilPerEenheid > 0 ? '+' : ''}{fmtEUR(a.verschilPerEenheid)}</td>
                    <td className={`px-1.5 py-2 text-right tabular-nums whitespace-nowrap ${kleur(a.verschilPerEenheid)}`}>{pct(a.verschilPct)}</td>
                    <td className={`px-1.5 py-2 text-right tabular-nums whitespace-nowrap font-bold ${kleur(a.impact)}`}>{a.impact > 0 ? '+' : ''}{fmtEUR(a.impact)}</td>
                    <td className="px-1.5 py-2 min-w-[140px]">
                      {editing && !a.bekend && onReden ? (
                        <>
                          <input
                            className={`input py-1 text-sm ${fout ? 'border-danger' : ''}`}
                            value={a.reden}
                            placeholder={`Waarom? (min. ${PRIJSAFWIJKING_REDEN_MIN} tekens)`}
                            onChange={(e) => onReden(a, e.target.value)}
                            aria-label={`Reden voor de afwijkende prijs van ${a.post}`}
                            aria-invalid={fout || undefined}
                          />
                          {fout && <p className="text-xs text-danger mt-1">Reden verplicht, anders wordt deze prijs niet opgeslagen.</p>}
                        </>
                      ) : (
                        a.reden || (a.vanVoorHistoriek ? <span className="text-secondary italic">van vóór de historiek</span> : '—')
                      )}
                    </td>
                    <td className="px-1.5 py-2">
                      {a.door ?? (a.bekend ? '—' : <span className="text-secondary italic">nog niet opgeslagen</span>)}
                      {a.op && <div className="text-xs text-secondary whitespace-nowrap">{fmtDate(a.op)}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="px-1.5 pt-3 text-right font-bold">Totale impact</td>
                <td className={`px-1.5 pt-3 text-right tabular-nums font-extrabold whitespace-nowrap ${kleur(impact)}`}>{impact > 0 ? '+' : ''}{fmtEUR(impact)}</td>
                <td colSpan={2} className="px-1.5 pt-3 text-secondary">{impactPct != null ? `${pct(impactPct)} van het calculatietotaal` : ''}</td>
              </tr>
            </tfoot>
          </table>
          <p className="text-xs text-secondary mt-2 normal-case tracking-normal">Rood = duurder voor Recotex, groen = goedkoper. Elke nieuwe of gewijzigde afwijking wordt bij opslaan gemeld aan wie boven de invoerder staat.</p>
        </div>
      )}
    </div>
  );
}

// === Open voor oplevering (27okt 4) ===

/** Keuze per post tijdens het bewerken: factureerbaar of open voor oplevering (+ datum en opmerking). */
export function OpleveringKeuze({ oplevering, onChange, uitgeschakeld }: {
  oplevering?: Oplevering | null;
  onChange: (o: Oplevering | undefined) => void;
  /** Bv. voor posten die de crepi-ladder prijst. */
  uitgeschakeld?: string;
}) {
  const open = oplevering?.status === 'open';
  if (uitgeschakeld) return <span className="text-[11px] text-secondary" title={uitgeschakeld}>—</span>;
  return (
    <div className="text-xs normal-case tracking-normal">
      <label className="inline-flex items-center gap-1 cursor-pointer" title="De onderaannemer krijgt de post op de calculatie, maar mag hem nog niet factureren">
        <input
          type="checkbox"
          checked={open}
          disabled={open && !!oplevering?.gezetOp}
          onChange={(e) => onChange(e.target.checked ? { status: 'open', verwachteDatum: null, opmerking: '' } : undefined)}
        />
        open voor oplevering
      </label>
      {open && (
        <div className="mt-1 flex flex-wrap gap-1 items-center">
          <input
            type="date"
            className="border border-border rounded px-1 py-0.5"
            value={oplevering?.verwachteDatum ?? ''}
            onChange={(e) => onChange({ ...oplevering!, verwachteDatum: e.target.value || null })}
            aria-label="Verwachte opleverdatum"
          />
          <input
            className="border border-border rounded px-1 py-0.5 w-40"
            placeholder="opmerking"
            value={oplevering?.opmerking ?? ''}
            maxLength={500}
            onChange={(e) => onChange({ ...oplevering!, opmerking: e.target.value })}
            aria-label="Opmerking bij open post"
          />
          {oplevering?.gezetOp && <span className="text-secondary">sinds {fmtDate(oplevering.gezetOp)} — vrijgeven via het blok open posten</span>}
        </div>
      )}
    </div>
  );
}

/** Chip in leesmodus. */
export function OpleveringChip({ oplevering }: { oplevering?: Oplevering | null }) {
  if (oplevering?.status === 'open') {
    return (
      <span className="ml-2 inline-flex items-center rounded-full border border-accent/50 px-2 py-0.5 text-[11px] font-bold text-accent-700" title={oplevering.opmerking || 'Nog niet factureren'}>
        open voor oplevering{oplevering.verwachteDatum ? ` · ${fmtDate(oplevering.verwachteDatum)}` : ''}
      </span>
    );
  }
  if (oplevering?.vrijgegevenOp) {
    return <span className="ml-2 chip-neutral" title={`Vrijgegeven door ${oplevering.vrijgegevenDoor ?? '—'}`}>vrijgegeven {fmtDate(oplevering.vrijgegevenOp)}</span>;
  }
  return null;
}

export function OpenPostenBlok({ projectId, openPosten, nuFactureerbaar, openVoorOplevering, vrijgaves, magVrijgeven, bevestigers, bezig, onVrijgeven, onMailAanvulling }: {
  projectId: string;
  openPosten: OpenPost[];
  nuFactureerbaar: number;
  openVoorOplevering: number;
  vrijgaves: VrijgaveRij[];
  magVrijgeven: boolean;
  bevestigers: string;
  bezig: boolean;
  onVrijgeven: (postIds: string[]) => void;
  onMailAanvulling: (vrijgaveId: string) => void;
}) {
  const [gekozen, setGekozen] = useState<Set<string>>(new Set());
  if (!openPosten.length && !vrijgaves.length) return null;
  const wissel = (id: string) => setGekozen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div id="open-posten" className="card mb-5 border-l-4 border-accent scroll-mt-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-lg font-bold">Open voor oplevering</h3>
        <div className="flex gap-6 normal-case tracking-normal">
          <span className="text-sm">Nu factureerbaar <strong className="tabular-nums">{fmtEUR(nuFactureerbaar)}</strong></span>
          <span className="text-sm">Open, nog niet factureren <strong className="tabular-nums text-accent-700">{fmtEUR(openVoorOplevering)}</strong></span>
        </div>
      </div>
      {openPosten.length > 0 && (
        <>
          <ul className="divide-y divide-lightgrey text-sm normal-case tracking-normal">
            {openPosten.map((o) => (
              <li key={o.id} className="py-2 flex items-center gap-3 flex-wrap">
                {magVrijgeven && <input type="checkbox" checked={gekozen.has(o.id)} onChange={() => wissel(o.id)} aria-label={`${o.naam} selecteren om vrij te geven`} />}
                <span className="font-semibold flex-1 min-w-[160px]">{o.naam}<span className="text-xs text-secondary"> ({o.soort === 'werkpost' ? 'werkpost' : o.soort === 'meerwerk' ? 'meerwerk' : 'extra werk'})</span></span>
                {o.hoeveelheid != null && <span className="tabular-nums text-secondary">{fmtNum(o.hoeveelheid)} {o.eenheid}</span>}
                <span className="text-secondary">{o.verwachteDatum ? `verwacht ${fmtDate(o.verwachteDatum)}` : 'geen datum'}</span>
                {o.opmerking && <span className="text-secondary italic">{o.opmerking}</span>}
                <strong className="tabular-nums">{fmtEUR(o.bedrag)}</strong>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {magVrijgeven ? (
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={bezig || !gekozen.size}
                onClick={() => {
                  if (confirm(`${gekozen.size} post(en) vrijgeven? De calculatie krijgt een nieuwe versie en er ontstaat een aanvulling voor de onderaannemer.`)) onVrijgeven([...gekozen]);
                }}
              >{bezig ? 'Vrijgeven…' : `Vrijgeven (${gekozen.size})`}</button>
            ) : (
              <span className="text-xs text-secondary normal-case tracking-normal">Vrijgeven kan enkel door {bevestigers}.</span>
            )}
            <span className="text-xs text-secondary normal-case tracking-normal">Open posten tellen mee in beide percentages: het is een vastgelegde kost.</span>
          </div>
        </>
      )}
      {vrijgaves.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <h4 className="text-sm font-bold mb-1">Aanvullingen</h4>
          <ul className="text-sm space-y-1 normal-case tracking-normal">
            {vrijgaves.map((v) => (
              <li key={v.id} className="flex items-center gap-3 flex-wrap">
                <span>{fmtDateTime(v.vrijgegevenOp)} · {v.vrijgegevenDoorNaam} · versie {v.versie}</span>
                <strong className="tabular-nums">{fmtEUR(v.bedrag)}</strong>
                <a className="underline" href={api.projects.aanvullingPdfUrl(projectId, v.id)} target="_blank" rel="noopener noreferrer">PDF</a>
                {v.verstuurdOp
                  ? <span className="chip-neutral">verstuurd {fmtDate(v.verstuurdOp)}</span>
                  : <button type="button" className="btn-ghost btn-sm" onClick={() => onMailAanvulling(v.id)}>✉ Naar onderaannemer</button>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// === Versies (SPEC_workflow 5.1) ===

const AANLEIDING: Record<string, string> = {
  aangemaakt: 'aangemaakt',
  opgeslagen: 'aangepast',
  stand_bij_activatie: 'stand bij activatie historiek',
  vrijgave: 'vrijgave open posten',
  nieuwe_versie: 'nieuwe versie na verzenden',
};

export function VersiesPaneel({ projectId, versies, huidigeVersie, verstuurdeVersie }: {
  projectId: string;
  versies: VersieRij[];
  huidigeVersie: number;
  verstuurdeVersie: number | null;
}) {
  if (!versies.length) return null;
  return (
    <details className="card mb-5">
      <summary className="cursor-pointer text-lg font-bold">
        Versies <span className="text-sm text-secondary font-normal normal-case tracking-normal">({versies.length}{verstuurdeVersie ? `, versie ${verstuurdeVersie} verstuurd` : ''})</span>
      </summary>
      <table className="w-full text-sm mt-3 normal-case tracking-normal">
        <thead>
          <tr className="thead-row">
            <th className="px-2 py-1">Versie</th><th className="px-2 py-1">Wat</th><th className="px-2 py-1">Door</th><th className="px-2 py-1">Wanneer</th>
            <th className="px-2 py-1 text-right">Totaal</th><th className="px-2 py-1">Verstuurd</th><th className="px-2 py-1"></th>
          </tr>
        </thead>
        <tbody>
          {[...versies].reverse().map((v) => (
            <tr key={v.versie} className="border-b border-lightgrey">
              <td className="px-2 py-1 font-bold">v{v.versie}{v.versie === huidigeVersie && <span className="text-xs text-secondary font-normal"> (huidig)</span>}</td>
              <td className="px-2 py-1">{AANLEIDING[v.aanleiding] ?? v.aanleiding}</td>
              <td className="px-2 py-1">{v.aangemaaktDoorNaam || '—'}</td>
              <td className="px-2 py-1 whitespace-nowrap">{fmtDateTime(v.aangemaaktOp)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(v.totaal)}</td>
              <td className="px-2 py-1">{v.verstuurdOp ? fmtDate(v.verstuurdOp) : '—'}</td>
              <td className="px-2 py-1"><a className="underline" href={api.projects.versiePdfUrl(projectId, v.versie)} target="_blank" rel="noopener noreferrer">bekijk PDF</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

// === Posten verschuiven (SPEC_workflow 7.2) ===

export function verschuif<T>(lijst: T[], index: number, richting: -1 | 1): T[] {
  const doel = index + richting;
  if (doel < 0 || doel >= lijst.length) return lijst;
  const kopie = [...lijst];
  [kopie[index], kopie[doel]] = [kopie[doel]!, kopie[index]!];
  return kopie;
}

export function VerschuifKnoppen({ index, aantal, onVerschuif }: { index: number; aantal: number; onVerschuif: (richting: -1 | 1) => void }) {
  return (
    <span className="inline-flex flex-col leading-none">
      <button type="button" className="text-secondary hover:text-primary disabled:opacity-30 px-1" disabled={index === 0} onClick={() => onVerschuif(-1)} aria-label="Post naar boven">▲</button>
      <button type="button" className="text-secondary hover:text-primary disabled:opacity-30 px-1" disabled={index === aantal - 1} onClick={() => onVerschuif(1)} aria-label="Post naar beneden">▼</button>
    </span>
  );
}
